import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Loader2, X, Pencil } from "lucide-react";

const PRODUCT_OPTIONS = [
  { id: "toolkit", label: "Manager's Toolkit ($99)" },
  { id: "library", label: "Operations Library ($499)" },
  { id: "cdos", label: "CDOS Complete ($1,999)" },
];

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [systems, setSystems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/documents"), api.get("/categories"), api.get("/systems"), api.get("/departments")])
      .then(([d, c, s, dep]) => { setDocs(d.data); setCategories(c.data); setSystems(s.data); setDepartments(dep.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;
  const visible = deptFilter === "all" ? docs : docs.filter((d) => d.department === deptFilter);

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Document deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Documents</h1>
          <p className="text-muted-foreground mt-2">{visible.length} of {docs.length} documents{deptFilter !== "all" ? ` in ${deptFilter}` : ""}.</p>
        </div>
        <button onClick={() => setShowUpload(true)} data-testid="admin-upload-open"
          className="inline-flex items-center gap-2 bg-primary text-white px-5 py-3 font-semibold btn-lift">
          <Upload className="w-4 h-4" /> Upload
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <FilterChip active={deptFilter === "all"} onClick={() => setDeptFilter("all")} testid="admin-dept-all">All departments</FilterChip>
        {departments.map((d) => (
          <FilterChip key={d.name} active={deptFilter === d.name} onClick={() => setDeptFilter(d.name)} testid={`admin-dept-${d.name.replace(/\s+/g, "-").toLowerCase()}`}>
            {d.name} ({d.count})
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
      ) : (
        <div className="bg-white border border-border mt-8 divide-y divide-border">
          {visible.map((d) => (
            <div key={d.id} data-testid={`admin-doc-${d.id}`} className="flex items-center gap-4 p-4">
              <div className="w-9 h-9 bg-secondary flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.code ? <span className="font-mono text-xs mr-2 text-primary">{d.code}</span> : null}{d.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {d.system ? <span className="mr-1 font-semibold">{d.system}</span> : null}{d.department ? <span className="mr-1">· {d.department}</span> : null}· {catName(d.category)} · in {(d.product_ids || []).length} product(s)
                  {d.is_sample && <span className="ml-2 text-accent">sample</span>}
                </div>
              </div>
              <button onClick={() => setEditing(d)} data-testid={`admin-doc-edit-${d.id}`}
                className="text-muted-foreground hover:text-primary p-2">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(d.id)} data-testid={`admin-doc-delete-${d.id}`}
                className="text-muted-foreground hover:text-accent p-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal categories={categories} systems={systems} departments={departments} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />
      )}
      {editing && (
        <EditDocModal doc={editing} categories={categories} systems={systems} departments={departments} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function FilterChip({ active, children, testid, ...props }) {
  return (
    <button {...props} data-testid={testid}
      className={`text-sm px-4 py-2 border transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary"}`}>
      {children}
    </button>
  );
}

function UploadModal({ categories, systems, departments, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", category: categories[0]?.slug || "", system: systems[0]?.code || "", department: "", description: "" });
  const [products, setProducts] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setProducts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file.");
    if (products.length === 0) return toast.error("Assign to at least one product.");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("system", form.system);
    fd.append("department", form.department);
    fd.append("description", form.description);
    fd.append("product_ids", products.join(","));
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      onDone();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-upload-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-heading font-bold text-xl">Upload document</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="upload-title"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="upload-category"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary bg-white">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">System</label>
            <select value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} data-testid="upload-system"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary bg-white">
              {systems.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.short}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Department</label>
            <input list="dept-options" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="upload-department"
              placeholder="e.g. Service, Sales, Human Resources"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
            <datalist id="dept-options">
              {(departments || []).map((d) => <option key={d.name} value={d.name} />)}
            </datalist>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Include in products</label>
            <div className="space-y-2">
              {PRODUCT_OPTIONS.map((p) => (
                <label key={p.id} className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={products.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`upload-product-${p.id}`} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">File</label>
            <input type="file" required onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file"
              className="w-full text-sm border border-border px-4 py-2.5" />
          </div>
          <button type="submit" disabled={busy} data-testid="upload-submit"
            className="w-full bg-primary text-white py-3 font-semibold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : "Upload document"}
          </button>
        </form>
      </div>
    </div>
  );
}


function EditDocModal({ doc, categories, systems, departments, onClose, onDone }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    category: doc.category || categories[0]?.slug || "",
    system: doc.system || systems[0]?.code || "",
    department: doc.department || "",
    description: doc.description || "",
  });
  const [products, setProducts] = useState(doc.product_ids || []);
  const [busy, setBusy] = useState(false);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);

  const toggle = (id) => setProducts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const doReplace = async () => {
    if (!replaceFile) return toast.error("Choose a PDF to replace with.");
    setReplacing(true);
    const fd = new FormData();
    fd.append("file", replaceFile);
    try {
      await api.post(`/admin/documents/${doc.id}/replace-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced. Buyers keep access; URL is unchanged.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replace failed.");
    } finally {
      setReplacing(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/documents/${doc.id}`, {
        title: form.title,
        category: form.category,
        system: form.system,
        department: form.department,
        description: form.description,
        product_ids: products,
      });
      toast.success("Document updated.");
      onDone();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-doc-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-heading font-bold text-xl">Edit {doc.code || "document"}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-2">System</label>
              <select value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} data-testid="edit-system"
                className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary bg-white">
                {systems.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.short}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-category"
                className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary bg-white">
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Department</label>
            <input list="edit-dept-options" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="edit-department"
              placeholder="e.g. Service, Sales, Human Resources"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
            <datalist id="edit-dept-options">
              {(departments || []).map((d) => <option key={d.name} value={d.name} />)}
            </datalist>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Included in packages</label>
            <div className="space-y-2">
              {PRODUCT_OPTIONS.map((p) => (
                <label key={p.id} className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={products.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`edit-product-${p.id}`} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-description"
              className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <button type="submit" disabled={busy} data-testid="edit-doc-save"
            className="w-full bg-primary text-white py-3 font-semibold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save changes"}
          </button>

          <div className="border-t border-border pt-4 mt-2">
            <label className="text-sm font-medium block mb-1">Replace PDF file</label>
            <p className="text-xs text-muted-foreground mb-2">
              Swap the file content in place — same code, same /forms/{doc.code} URL, buyers keep access.
              {doc.updated_at && <> Last updated {new Date(doc.updated_at).toLocaleDateString()}.</>}
            </p>
            <input type="file" accept="application/pdf" onChange={(e) => setReplaceFile(e.target.files[0])} data-testid="replace-file-input"
              className="w-full text-sm border border-border px-4 py-2.5" />
            <button type="button" onClick={doReplace} disabled={replacing || !replaceFile} data-testid="replace-file-btn"
              className="w-full mt-2 border border-border py-2.5 font-semibold hover:border-primary disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {replacing ? <><Loader2 className="w-4 h-4 animate-spin" /> Replacing…</> : <><Upload className="w-4 h-4" /> Replace file</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
